import React, { useState, useRef, useEffect } from 'react';
import Button from '../button';
import { MaterialIconFilled } from '../../utils/global';
import Tooltip from '../tooltip';

interface VoiceRecorderProps {
  handleAudioApiCall: (audioBlob: Blob) => Promise<void>;
}

const VoiceRecorder: React.FC<VoiceRecorderProps> = ({ handleAudioApiCall }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [hasError, setHasError] = useState(false);
  const audioChunks = useRef<Int16Array[]>([]);
  const audioStream = useRef<MediaStream | null>(null);
  const audioContext = useRef<AudioContext | null>(null);
  const audioWorkletNode = useRef<AudioWorkletNode | null>(null);
  const silenceTimer = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      // Cleanup only active resources
      if (audioContext.current?.state !== 'closed') {
        audioContext.current?.close();
      }
      audioStream.current?.getTracks().forEach(track => track.stop());
    };
  }, []);

  const createWavBuffer = (chunks: Int16Array[]): ArrayBuffer => {
    const totalSamples = chunks.reduce((acc, chunk) => acc + chunk.length, 0);
    const buffer = new ArrayBuffer(44 + totalSamples * 2);
    const view = new DataView(buffer);

    // WAV header
    view.setUint32(0, 0x52494646); // "RIFF"
    view.setUint32(4, 36 + totalSamples * 2, true);
    view.setUint32(8, 0x57415645); // "WAVE"
    view.setUint32(12, 0x666d7420); // "fmt "
    view.setUint32(16, 16, true);
    view.setUint16(20, 1, true);
    view.setUint16(22, 1, true);
    view.setUint32(24, 16000, true);
    view.setUint32(28, 32000, true);
    view.setUint16(32, 2, true);
    view.setUint16(34, 16, true);
    view.setUint32(36, 0x64617461); // "data"
    view.setUint32(40, totalSamples * 2, true);

    // Write PCM data
    const int16View = new Int16Array(buffer, 44);
    let offset = 0;
    chunks.forEach(chunk => {
      int16View.set(chunk, offset);
      offset += chunk.length;
    });
    
    return buffer;
  };

  const initializeMicrophone = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      audioStream.current = stream;

      const context = new AudioContext({ sampleRate: 16000 });
      audioContext.current = context;

      await context.audioWorklet.addModule('/processor.js');
      const source = context.createMediaStreamSource(stream);
      const workletNode = new AudioWorkletNode(context, 'audio-processor');
      audioWorkletNode.current = workletNode;

      workletNode.port.onmessage = (event: MessageEvent) => {
        if (event.data.audioBuffer) {
          audioChunks.current.push(new Int16Array(event.data.audioBuffer));
        }
      };

      source.connect(workletNode).connect(context.destination);
    } catch (error) {
      console.error('Microphone error:', error);
      setHasError(true);
    }
  };

  const startRecording = async () => {
    if (!audioContext.current || audioContext.current.state === 'closed') {
      await initializeMicrophone();
    }

    setIsRecording(true);
    audioChunks.current = [];
    detectSilence();
  };

  const stopRecording = async () => {
    setIsRecording(false);
    if (silenceTimer.current) window.clearTimeout(silenceTimer.current);

    // Cleanup audio processing nodes
    audioWorkletNode.current?.disconnect();
    audioWorkletNode.current = null;

    // Stop media stream first
    if (audioStream.current) {
      audioStream.current.getTracks().forEach(track => track.stop());
      audioStream.current = null;
    }

    // Process remaining audio
    if (audioChunks.current.length > 0) {
      try {
        const wavBuffer = createWavBuffer(audioChunks.current);
        const audioBlob = new Blob([wavBuffer], { type: 'audio/wav' });
        await handleAudioApiCall(audioBlob);
      } catch (error) {
        console.error('Audio processing error:', error);
      } finally {
        audioChunks.current = [];
      }
    }

    // Close context last if still open
    if (audioContext.current?.state !== 'closed') {
      await audioContext.current?.close();
      audioContext.current = null;
    }
  };

  const detectSilence = () => {
    silenceTimer.current = window.setTimeout(stopRecording, 3000);
  };

  return (
    <div style={{ textAlign: 'center' }}>
      {hasError ? (
        <Tooltip tooltipText="Microphone access required" variant="dark" size="lg">
          <span className="material-symbols-sharp" style={MaterialIconFilled}>
            error
          </span>
        </Tooltip>
      ) : (
        <Button
          onClick={isRecording ? stopRecording : startRecording}
          title=''
          variant={isRecording ? 'rounded-icon-secondary' : 'rounded-icon-secondary-dark'}
          icon={isRecording ? 'stop' : 'mic'}
        />
      )}
    </div>
  );
};

export default VoiceRecorder;